import React, { useState, useRef, useEffect } from "react";
import {
    Button, Box, Container, Header, Link, SpaceBetween, StatusIndicator,
    Modal, Table, Spinner, FormField, Input
} from "@amzn/awsui-components-react/polaris";
import ChatBubble from "@amzn/awsui-chat-components-console/chat-bubble";
import ButtonGroup from "@amzn/awsui-components-console/button-group";
import PromptInput from "@amzn/awsui-components-console/prompt-input";
import Avatar from "@amzn/awsui-chat-components-console/avatar";
import ReactMarkdown from "react-markdown";
import { FremontExpandableSection } from "../../utils/CommonComponents";
import WorkflowSelector from "./WorkflowSelector";
import ClickableSuggestions from "./ClickableSuggestions";
import { COMPONENT_TYPE_SUGGESTIONS } from "./Chatbot";

const fontFamily = "Monaco, Consolas, Liberation Mono, Courier New, monospace";

// Function to get chat bubble props based on message type
const getChatBubbleProps = (isUserMessage, messageText, familyName, givenName) => {
    const fullName = `${givenName} ${familyName}`;
    const initials = `${givenName[0]}${familyName[0]}`;
    if (isUserMessage) {
        return {
            avatar: (
                <Avatar
                    ariaLabel={fullName}
                    tooltipText={fullName}
                    initials={initials}
                />
            ),
            type: "outgoing",
            ariaLabel: initials
        };
    }
    return {
        ariaLabel: "Lighthouse Assistance at 6:35:10pm",
        type: "incoming",
        actions: (
            <ButtonGroup
                onItemClick={({ detail }) =>
                    detail.id === "copy" && navigator.clipboard.writeText(messageText)}
                ariaLabel="Chat bubble actions"
                variant="icon"
                items={[
                    {
                        type: "icon-button",
                        id: "copy",
                        iconName: "copy",
                        text: "Copy",
                        popoverFeedback: (
                            <StatusIndicator type="success">
                                Message copied
                            </StatusIndicator>
                        )
                    }
                ]}
            />
        ),
        avatar: (
            <Avatar
                color="gen-ai"
                iconName="gen-ai"
                ariaLabel="Lighthouse Assistance"
                tooltipText="Lighthouse Assistance"
            />
        )
    };
};

const renderMessageAsMarkdown = markdownText => (
    <ReactMarkdown
        components={{
            // Custom renderer for code (handles both inline and block)
            code: ({ className, children, ...props }) => {
                // Check if it's inline code (no className means inline)
                if (!className) {
                    return (
                        <code
                            style={{
                                backgroundColor: "#f6f8fa",
                                border: "1px solid #e1e4e8",
                                borderRadius: "3px",
                                padding: "2px 4px",
                                fontSize: "13px",
                                fontFamily
                            }}
                            {...props}
                        >
                            {children}
                        </code>
                    );
                }
                // For code blocks, use the block renderer
                return (
                    <pre
                        style={{
                            backgroundColor: "#f6f8fa",
                            border: "1px solid #e1e4e8",
                            borderRadius: "4px",
                            padding: "8px",
                            margin: "8px 0",
                            overflow: "auto",
                            fontSize: "13px",
                            fontFamily
                        }}
                    >
                        <code className={className} {...props}>
                            {children}
                        </code>
                    </pre>
                );
            },
            // Custom renderer for strong/bold text
            strong: ({ children }) => (
                <strong style={{ fontWeight: "bold" }}>{children}</strong>
            ),
            // Custom renderer for emphasis/italic text
            em: ({ children }) => (
                <em style={{ fontStyle: "italic" }}>{children}</em>
            ),
            // Custom renderer for paragraphs
            p: ({ children }) => (
                <p style={{ margin: "8px 0" }}>{children}</p>
            ),
            // Custom renderer for horizontal rules
            hr: () => (
                <hr style={{
                    border: "none",
                    borderTop: "1px solid #e1e4e8",
                    margin: "12px 0"
                }}
                />
            )
        }}
    >
        {markdownText}
    </ReactMarkdown>
);

// Common component to render formatted JSON data
const renderFormattedJson = (label, data) => (
    <div>
        <div style={{ marginBottom: "4px", fontWeight: "bold" }}>{label}:</div>
        <pre
            style={{
                backgroundColor: "#f6f8fa",
                border: "1px solid #e1e4e8",
                borderRadius: "4px",
                padding: "12px",
                margin: "0",
                overflow: "auto",
                fontSize: "13px",
                fontFamily,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word"
            }}
        >
            {typeof data === "string" ? data : JSON.stringify(data, null, 2)}
        </pre>
    </div>
);

const renderToolUse = toolUse => (
    <FremontExpandableSection header="Tool Use">
        <SpaceBetween size="xs">
            <Box fontWeight="bold">{`Tool Used: ${toolUse.name}`}</Box>
            {renderFormattedJson("Input", toolUse.input)}
        </SpaceBetween>
    </FremontExpandableSection>
);

const renderToolResult = toolResult => (
    <FremontExpandableSection header="Tool Result">
        <SpaceBetween size="xs">
            <Box fontWeight="bold">{`Status: ${toolResult.status}`}</Box>
            {toolResult && toolResult.content && renderFormattedJson("Content", toolResult.content[0].text)}
        </SpaceBetween>
    </FremontExpandableSection>
);

const ChatbotPage = ({
    messages,
    inputValue,
    onInputChange,
    sendMessage,
    handlePromptInputAction,
    isLoading,
    familyName,
    givenName,
    // Suggestion click handler
    onSuggestionClick,
    // Session management props
    currentSessionId,
    sessions,
    isLoadingSessions,
    isLoadingSession,
    loadingSessionId,
    showSessionList,
    onCreateNewSession,
    onSelectSession,
    onToggleSessionList,
    // Pagination props
    hasMoreSessions,
    isLoadingMoreSessions,
    onLoadMoreSessions,
    userId,
    nestUser
}) => {
    // Workflow modal state
    const [showWorkflowModal, setShowWorkflowModal] = useState(false);

    // Session ID input state
    const [sessionIdInput, setSessionIdInput] = useState("");

    // Username input state (for NEST users)
    const [usernameInput, setUsernameInput] = useState(userId);

    // Ref for auto-scrolling to bottom
    const messagesEndRef = useRef(null);

    // Auto-scroll to bottom when messages change or loading state changes
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: "smooth",
                block: "end"
            });
        }
    }, [messages, isLoading]);

    // Handle workflow application
    const handleApplyWorkflow = async (prompt) => {
        // Directly send the message with the workflow prompt
        // This is similar to how suggestion clicks work
        await sendMessage(prompt);
    };

    // Handle loading session by ID
    const handleLoadSessionById = () => {
        const trimmedSessionId = sessionIdInput.trim();
        if (trimmedSessionId) {
            onSelectSession(trimmedSessionId, usernameInput);
            setSessionIdInput(""); // Clear the input after submitting
        }
    };

    // Handle Enter key press in session ID input
    const handleSessionIdKeyPress = (event) => {
        if (event.detail.key === "Enter") {
            handleLoadSessionById();
        }
    };

    return (
        <Box
            padding="l"
        >
            <Container
                header={
                    <Header
                        variant="h3"
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                    variant="normal"
                                    iconName="add-plus"
                                    onClick={onCreateNewSession}
                                >
                                    New Chat
                                </Button>
                                <Button
                                    variant="normal"
                                    iconName="folder"
                                    onClick={() => setShowWorkflowModal(true)}
                                >
                                    Workflows
                                </Button>
                                <Button
                                    variant="normal"
                                    iconName="view-horizontal"
                                    onClick={onToggleSessionList}
                                    loading={isLoadingSessions}
                                >
                                    Sessions
                                </Button>
                            </SpaceBetween>
                        }
                        description={currentSessionId ? `Session: ${currentSessionId}` : "No active session"}
                    >
                        Lighthouse Chat
                    </Header>
                }
                footer={
                    <>
                        <PromptInput
                            value={inputValue}
                            onAction={handlePromptInputAction}
                            onChange={({ detail }) => onInputChange(detail.value)}
                            placeholder="Ask me about Lighthouse links, hierarchies, orders, or geospatial data..."
                            actionButtonIconName={isLoading ? "close" : "send"}
                            minRows={2}
                            maxRows={8}
                        />
                        <Box color="text-body-secondary" margin={{ top: "xs" }} fontSize="body-s">
                            Use of this service is subject to the{" "}
                            <Link
                                href="https://aws.amazon.com/ai/responsible-ai/policy/"
                                external
                                variant="primary"
                                fontSize="inherit"
                            >
                                AWS Responsible AI Policy
                            </Link>
                            .
                        </Box>
                    </>
                }
            >
                <div style={{
                    // TODO: Make this dynamic
                    maxHeight: "calc(65vh)",
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column"
                }}
                >
                    <Box
                        padding="s"
                        backgroundColor="background-container-content"
                        overflow="auto"
                    >
                        <SpaceBetween size="xs">
                            {messages.map((message, index) => {
                                // Handle both old and new message formats for backward compatibility
                                const isUserMessage = message.role === "user";
                                let messageText;
                                let toolUse;
                                let toolResult;
                                let suggestionsComponent = null;

                                if (message.error) {
                                    messageText = message.error;
                                } else if (message.content) {
                                    // Check if this is a suggestions component
                                    if (message.content[0].componentType === COMPONENT_TYPE_SUGGESTIONS) {
                                        suggestionsComponent = (
                                            <ClickableSuggestions
                                                suggestions={message.content[0].suggestions}
                                                onSuggestionClick={onSuggestionClick}
                                            />
                                        );
                                    } else {
                                        messageText = message.content[0].text;
                                        toolUse = message.content[1] && message.content[1].toolUse;
                                        // eslint-disable-next-line prefer-destructuring
                                        ({ toolResult } = message.content[0]);
                                    }
                                } else {
                                    messageText = "Unknown error parsing message response."
                                        + " Tell the agent to try again and if the issue"
                                        + " persists reach out to Lighthouse team for support.";
                                }

                                let textToCopy;
                                if (messageText) {
                                    textToCopy = messageText;
                                } else if (toolResult) {
                                    textToCopy = toolResult.content[0].text;
                                }

                                const chatBubbleProps = getChatBubbleProps(
                                    isUserMessage, textToCopy, familyName, givenName
                                );

                                return (
                                    <Box
                                        key={message.id || index}
                                        textAlign={isUserMessage ? "right" : "left"}
                                        margin={{ bottom: "xs" }}
                                    >
                                        <ChatBubble
                                            {...chatBubbleProps}
                                        >
                                            <SpaceBetween size="xs">
                                                {toolResult && renderToolResult(toolResult)}
                                                {suggestionsComponent && suggestionsComponent}
                                                {messageText && renderMessageAsMarkdown(messageText)}
                                                {toolUse && renderToolUse(toolUse)}
                                            </SpaceBetween>
                                        </ChatBubble>
                                    </Box>
                                );
                            })}
                            {isLoading && (

                                <ChatBubble
                                    ariaLabel="Lighthouse Assistance is thinking..."
                                    type="incoming"
                                    avatar={
                                        <Avatar
                                            loading
                                            color="gen-ai"
                                            iconName="gen-ai"
                                            ariaLabel="Lighthouse Assistance"
                                            tooltipText="Lighthouse Assistance"
                                        />
                                    }
                                >
                                    <Box color="text-status-inactive">
                                        Agent is thinking
                                    </Box>
                                </ChatBubble>

                            )}
                            {/* Invisible element to scroll to */}
                            <div ref={messagesEndRef}/>
                        </SpaceBetween>
                    </Box>
                </div>
            </Container>

            {/* Workflow Selector */}
            <WorkflowSelector
                visible={showWorkflowModal}
                onDismiss={() => setShowWorkflowModal(false)}
                onApplyWorkflow={handleApplyWorkflow}
            />

            {/* Session List Modal */}
            <Modal
                onDismiss={onToggleSessionList}
                visible={showSessionList}
                header="Chat Sessions"
                size="max"
            >
                <SpaceBetween size="l">
                    <SpaceBetween direction="horizontal" size="xs">
                        {nestUser && (
                            <FormField
                                label="Username"
                                description="Enter a username to view sessions for any user"
                            >
                                <Input
                                    value={usernameInput}
                                    onChange={({ detail }) => setUsernameInput(detail.value)}
                                    placeholder="Enter username..."
                                    disabled={isLoadingSession}
                                />
                            </FormField>
                        )}
                        <FormField
                            label="Session ID"
                            description="Enter a session ID to load that specific conversation"
                        >
                            <Input
                                value={sessionIdInput}
                                onChange={({ detail }) => setSessionIdInput(detail.value)}
                                onKeyDown={handleSessionIdKeyPress}
                                placeholder="Enter session ID..."
                                disabled={isLoadingSession}
                            />
                        </FormField>
                        <Box padding={{ top: "xxxl" }} margin={{ top: "xxxs" }}>
                            <Button
                                variant="primary"
                                onClick={handleLoadSessionById}
                                disabled={!sessionIdInput.trim() || isLoadingSession}
                                loading={isLoadingSession && loadingSessionId === sessionIdInput.trim()}
                            >
                                Load Session
                            </Button>
                        </Box>
                    </SpaceBetween>
                    {isLoadingSessions ? (
                        <Box textAlign="center" padding="l">
                            <Spinner size="large"/>
                            <Box margin={{ top: "s" }}>Loading sessions...</Box>
                        </Box>
                    ) : (
                        <Box>
                            <Table
                                columnDefinitions={[
                                    {
                                        id: "sessionId",
                                        header: "Session",
                                        cell: item => (
                                            <Box>
                                                <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                                                    {item.firstMessage}
                                                </div>
                                                {item.firstMessage && item.firstMessage !== "New session" ? (
                                                    <div style={{
                                                        fontSize: "13px",
                                                        color: "#333",
                                                        marginTop: "4px",
                                                        maxWidth: "400px",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap"
                                                    }}
                                                    >
                                                        {item.sessionId}
                                                    </div>
                                                ) : (
                                                    <div style={{
                                                        fontSize: "13px",
                                                        color: "#999",
                                                        marginTop: "4px",
                                                        fontStyle: "italic"
                                                    }}
                                                    >
                                                        New conversation
                                                    </div>
                                                )}
                                            </Box>
                                        ),
                                        minWidth: 450
                                    },
                                    {
                                        id: "actions",
                                        header: "Actions",
                                        cell: item => (
                                            <Button
                                                variant="primary"
                                                size="small"
                                                onClick={() => {
                                                    onSelectSession(item.sessionId);
                                                }}
                                                disabled={item.sessionId === currentSessionId || isLoadingSession}
                                                loading={loadingSessionId === item.sessionId}
                                            >
                                                {item.sessionId === currentSessionId ? "Current" : "Load"}
                                            </Button>
                                        ),
                                        minWidth: 100
                                    }
                                ]}
                                items={sessions || []}
                                empty={
                                    <Box textAlign="center" color="inherit" padding="l">
                                        <Box variant="strong" color="inherit">
                                            No sessions found
                                        </Box>
                                        <Box padding={{ top: "s" }} variant="p" color="inherit">
                                            Start a new conversation to create your first session.
                                        </Box>
                                    </Box>
                                }
                                header={
                                    <Header
                                        variant="h3"
                                        counter={sessions ? `(${sessions.length})` : "(0)"}
                                        actions={
                                            <SpaceBetween
                                                direction="horizontal"
                                                size="xs"
                                            >
                                                <Button
                                                    variant="primary"
                                                    onClick={onLoadMoreSessions}
                                                    loading={isLoadingMoreSessions}
                                                    disabled={!hasMoreSessions || isLoadingMoreSessions}
                                                >
                                                    Load More Sessions
                                                </Button>
                                            </SpaceBetween>
                                        }
                                    >
                                        Recent Sessions
                                    </Header>
                                }
                                variant="borderless"
                                wrapLines
                            />

                        </Box>
                    )}
                </SpaceBetween>
            </Modal>
        </Box>
    );
};

export default ChatbotPage;
